/* eslint-disable */
"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  UsersRound, Wallet, HandCoins, Landmark, ArrowRight, CalendarRange,
  TrendingUp, UserPlus, FileSpreadsheet, PieChart as PieIcon, Loader2,
} from "lucide-react";
import { Card, Pill, EmptyState } from "./ui";
import { RollingNumber } from "./rolling-number";
import { PageHeader } from "./sidebar";
import { DeptCostBars, EmployeeTypeDonut, PayrollTrendChart } from "./dashboard-charts";
import { formatDate, formatPeso, monthLabel } from "./utils";
import { Link, useFilterStore, FiltersProvider, useRouterState } from "./router";
import {
  getDashboard, listEmployees, listLoans, listPayroll, getPayrollDetail, getPreferences,
} from "./api";
import { EmployeesClient } from "./employees-client";
import { LoansClient } from "./loans-client";
import { PayrollClient, PAYROLL_BRAND } from "./payroll-client";
import { PayrollDetailClient } from "./payroll-detail-client";
import { PreferencesClient } from "./preferences-client";

/* ----------------------------- data fetch helper ---------------------------- */
function useAsync<T>(fetcher: () => Promise<T>, deps: any[], debounceMs = 0): { data: T | null; loading: boolean } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const first = useRef(true);
  useEffect(() => {
    let alive = true;
    const run = () => {
      setLoading(true);
      fetcher()
        .then((d) => { if (alive) setData(d); })
        .catch(() => { if (alive) setData(null); })
        .finally(() => { if (alive) setLoading(false); });
    };
    if (first.current || debounceMs === 0) {
      first.current = false;
      run();
      return () => { alive = false; };
    }
    const t = setTimeout(run, debounceMs);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line
  }, deps);
  return { data, loading };
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-32 text-faint" data-testid="payora-loading">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

/* -------------------------------- dashboard --------------------------------- */
function StatCard({ icon, label, value, sub, accent = false }: { icon: ReactNode; label: string; value: string; sub: string; accent?: boolean }) {
  return (
    <Card className="rise-in group relative overflow-hidden p-5">
      <div className="pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: accent ? "var(--glow-yellow)" : "var(--glow-blue)", opacity: 0.65 }} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">{label}</p>
          <RollingNumber text={value} className="font-display mt-2 text-[26px] leading-none font-bold tracking-tight text-text" />
          <p className="mt-2 text-xs font-medium text-muted">{sub}</p>
        </div>
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent ? "bg-[color-mix(in_oklab,var(--accent)_16%,transparent)] text-accent" : "bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] text-primary-strong"}`}>
          {icon}
        </span>
      </div>
    </Card>
  );
}

function DashboardPage() {
  const { refreshNonce } = useRouterState();
  const { data, loading } = useAsync<any>(getDashboard, [refreshNonce]);
  if (loading && !data) return <Loading />;
  const d = data || {};
  const recent = d.recentPeriods || [];
  const latest = d.latest || null;
  const trend = (d.trend || []).map((p: any) => ({
    label: `${formatDate(p.startDate).replace(/, \d{4}/, "")} \u2013 ${formatDate(p.endDate).split(",")[0]}`,
    gross: Number(p.gross), net: Number(p.net),
  }));
  const now = new Date();
  return (
    <>
      <PageHeader
        eyebrow={monthLabel(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`)}
        title="Payroll at a glance"
        description="Track workforce cost, loan exposure, and payout history across all secured payroll periods."
        actions={
          <>
            <Link href="/employees" className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-surface2 px-4 text-sm font-semibold text-text transition-all hover:border-border-strong hover:bg-surface3">
              <UserPlus className="h-4 w-4" /> Enroll employee
            </Link>
            <Link href="/payroll" className="inline-flex h-10 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent transition-all hover:bg-accent-strong active:scale-[0.97]">
              <Wallet className="h-4 w-4" /> Run payroll
            </Link>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={<UsersRound className="h-5 w-5" />} label="Active employees" value={String(d.activeCount ?? 0)}
          sub={`${d.t313 ?? 0} type 313 \u00b7 ${d.t261 ?? 0} type 261 \u00b7 ${d.inactive ?? 0} inactive`} />
        <StatCard icon={<Wallet className="h-5 w-5" />} label="Latest payout \u00b7 net"
          value={latest ? formatPeso(Number(latest.totalNet)) : "\u2014"} sub={latest ? latest.name : "No payroll generated yet"} accent />
        <StatCard icon={<HandCoins className="h-5 w-5" />} label="Active loan balance" value={formatPeso(Number(d.loanBalance ?? 0))}
          sub={`${d.loanCount ?? 0} active loan${(d.loanCount ?? 0) === 1 ? "" : "s"} on file`} />
        <StatCard icon={<Landmark className="h-5 w-5" />} label="Net paid YTD" value={formatPeso(Number(d.ytdNet ?? 0))}
          sub={`Across ${recent.length} finalized run${recent.length === 1 ? "" : "s"}`} accent />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="rise-in p-5 sm:p-6 xl:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="font-display flex items-center gap-2 text-base font-bold text-text">
                <TrendingUp className="h-4 w-4 text-primary-strong" /> Payroll cost trend
              </h2>
              <p className="mt-0.5 text-xs text-muted">Gross vs. net across recent finalized periods</p>
            </div>
            <div className="flex items-center gap-4 text-[11px] font-semibold text-muted">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Gross</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-accent" /> Net</span>
            </div>
          </div>
          <PayrollTrendChart data={trend} />
        </Card>

        <Card className="rise-in p-5 sm:p-6">
          <h2 className="font-display mb-1 flex items-center gap-2 text-base font-bold text-text">
            <PieIcon className="h-4 w-4 text-accent" /> Workforce mix
          </h2>
          <p className="mb-2 text-xs text-muted">Active employees by engagement type</p>
          <EmployeeTypeDonut
            data={[{ name: "Type 313", value: d.t313 ?? 0 }, { name: "Type 261", value: d.t261 ?? 0 }].filter((x) => x.value > 0)}
            total={d.activeCount ?? 0}
          />
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between rounded-xl bg-surface2 px-3.5 py-2.5 text-xs font-semibold">
              <span className="flex items-center gap-2 text-text"><span className="h-2 w-2 rounded-full bg-primary" /> Type 313</span>
              <span className="flex items-center gap-1 text-muted"><RollingNumber text={String(d.t313 ?? 0)} /> employees</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-surface2 px-3.5 py-2.5 text-xs font-semibold">
              <span className="flex items-center gap-2 text-text"><span className="h-2 w-2 rounded-full bg-accent" /> Type 261</span>
              <span className="flex items-center gap-1 text-muted"><RollingNumber text={String(d.t261 ?? 0)} /> employees</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="rise-in p-5 sm:p-6">
          <h2 className="font-display mb-1 text-base font-bold text-text">Cost per department</h2>
          <p className="mb-5 text-xs text-muted">{latest ? `Gross pay \u00b7 ${latest.name}` : "Gross pay for the latest period"}</p>
          <DeptCostBars data={d.deptData || []} />
        </Card>

        <Card className="rise-in overflow-hidden xl:col-span-2">
          <div className="flex items-center justify-between px-5 pt-5 sm:px-6">
            <div>
              <h2 className="font-display flex items-center gap-2 text-base font-bold text-text">
                <CalendarRange className="h-4 w-4 text-primary-strong" /> Recent payroll periods
              </h2>
              <p className="mt-0.5 text-xs text-muted">Secured, finalized payout runs</p>
            </div>
            <Link href="/payroll" className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-strong transition-colors hover:text-primary">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <EmptyState icon={<FileSpreadsheet className="h-6 w-6" />} title="No payroll runs yet"
              description="Generate your first payroll period to see payout history here." />
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-border bg-surface2 text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                    <th className="px-5 py-3 sm:px-6">Period</th>
                    <th className="px-4 py-3">Coverage</th>
                    <th className="px-4 py-3 text-right">Employees</th>
                    <th className="px-4 py-3 text-right">Gross</th>
                    <th className="px-5 py-3 text-right sm:px-6">Net pay</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.slice(0, 5).map((p: any) => (
                    <tr key={p.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-surface2/70">
                      <td className="px-5 py-3.5 sm:px-6">
                        <Link href={`/payroll/${p.id}`} className="font-semibold text-text hover:text-primary-strong">{p.name}</Link>
                        <div className="mt-1"><Pill tone="green">Finalized</Pill></div>
                      </td>
                      <td className="px-4 py-3.5 text-xs font-medium whitespace-nowrap text-muted">{formatDate(p.startDate)} &mdash; {formatDate(p.endDate)}</td>
                      <td className="px-4 py-3.5 text-right font-semibold text-muted">{p.employeeCount}</td>
                      <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap text-muted">{formatPeso(Number(p.totalGross), { decimals: 0 })}</td>
                      <td className="px-5 py-3.5 text-right sm:px-6"><RollingNumber text={formatPeso(Number(p.totalNet))} className="font-display justify-end font-bold text-accent" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/* --------------------------------- wrappers --------------------------------- */
function EmployeesPage() {
  const { refreshNonce } = useRouterState();
  const store = useFilterStore({ q: "", type: "", status: "", dept: "" });
  const { data, loading } = useAsync<any>(() => listEmployees(store.values), [store.values, refreshNonce], 260);
  if (loading && !data) return <Loading />;
  const d = data || { rows: [], departments: [], stats: { total: 0, t313: 0, t261: 0, active: 0 } };
  return (
    <FiltersProvider store={store}>
      <EmployeesClient rows={d.rows} departments={d.departments} stats={d.stats} initialFilters={store.values as any} />
    </FiltersProvider>
  );
}

function LoansPage() {
  const { refreshNonce } = useRouterState();
  const store = useFilterStore({ q: "", status: "", type: "" });
  const { data, loading } = useAsync<any>(() => listLoans(store.values), [store.values, refreshNonce], 260);
  if (loading && !data) return <Loading />;
  const d = data || {
    rows: [],
    loanTypes: [],
    activeEmployees: [],
    stats: { activeCount: 0, activeBalance: "0", paidCount: 0, totalPrincipal: "0" },
    canEditLoans: undefined,
  };
  return (
    <FiltersProvider store={store}>
      <LoansClient
        rows={d.rows}
        loanTypes={d.loanTypes}
        activeEmployees={d.activeEmployees}
        stats={d.stats}
        canEditLoans={d.canEditLoans}
        initialFilters={store.values as any}
      />
    </FiltersProvider>
  );
}

function PayrollListPage({ ledger }: { ledger: string }) {
  const { refreshNonce } = useRouterState();
  const store = useFilterStore({ q: "", status: "" });
  const { data, loading } = useAsync<any>(() => listPayroll(ledger, store.values), [store.values, refreshNonce, ledger], 260);
  if (loading && !data) return <Loading />;
  const d = data || { periods: [], holidayCounts: {}, activeEmployees: 0, totals: { runs: 0, net: "0", gross: "0" }, presets: [] };
  const brand = ledger === "mobility"
    ? {
        basePath: "/mobility-fund", eyebrow: "Mobility Fund", title: "Mobility Fund",
        description: "A dedicated mobility fund ledger running the exact payroll machinery \u2014 generate runs with holiday rules, keep finalized records locked, and manage them with an admin PIN.",
      }
    : PAYROLL_BRAND;
  return (
    <FiltersProvider store={store}>
      <PayrollClient periods={d.periods} holidayCounts={d.holidayCounts} activeEmployees={d.activeEmployees}
        totals={d.totals} presets={d.presets} brand={brand} initialFilters={store.values as any} />
    </FiltersProvider>
  );
}

function PayrollDetailPage({ id, ledger }: { id: number; ledger: string }) {
  const { refreshNonce } = useRouterState();
  const store = useFilterStore({ q: "", type: "", dept: "" });
  const { data, loading } = useAsync<any>(() => getPayrollDetail(id, ledger, store.values), [store.values, refreshNonce, id, ledger], 260);
  if (loading && !data) return <Loading />;
  if (!data || data.error || !data.period) {
    return <div className="py-32 text-center text-sm text-faint">Payroll record not found.</div>;
  }
  const basePath = ledger === "mobility" ? "/mobility-fund" : "/payroll";
  const backLabel = ledger === "mobility" ? "mobility fund" : "payroll periods";
  return (
    <FiltersProvider store={store}>
      <PayrollDetailClient period={data.period} holidays={data.holidays} rows={data.rows}
        departments={data.departments} basePath={basePath} backLabel={backLabel} initialFilters={store.values as any} />
    </FiltersProvider>
  );
}

function PreferencesPage() {
  const { refreshNonce } = useRouterState();
  const { data, loading } = useAsync<any>(getPreferences, [refreshNonce]);
  if (loading && !data) return <Loading />;
  const d = data || { sssDraft: [], sssLive: [], taxDraft: [], taxLive: [], presetDraft: [], presetLive: [] };
  return (
    <PreferencesClient sssDraft={d.sssDraft} sssLive={d.sssLive} taxDraft={d.taxDraft} taxLive={d.taxLive}
      presetDraft={d.presetDraft} presetLive={d.presetLive} />
  );
}

/* --------------------------------- router ----------------------------------- */
const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 14, filter: "blur(4px)" },
  enter: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -10, filter: "blur(6px)" },
};
const PAGE_TRANSITION = { duration: 0.48, ease: [0.22, 1, 0.36, 1] };

function routeTint(path: string): string | null {
  if (path === "/payroll" || /^\/payroll\/\d+$/.test(path)) return "color-mix(in oklab, var(--primary) 12%, transparent)";
  if (path === "/mobility-fund" || /^\/mobility-fund\/\d+$/.test(path)) return "color-mix(in oklab, var(--accent) 14%, transparent)";
  return null;
}

export function PayoraRoutes() {
  const { path } = useRouterState();
  const page = (() => {
    if (path === "/") return <DashboardPage />;
    if (path === "/employees") return <EmployeesPage />;
    if (path === "/loans") return <LoansPage />;
    if (path === "/preferences") return <PreferencesPage />;
    const payrollDetail = path.match(/^\/payroll\/(\d+)$/);
    if (payrollDetail) return <PayrollDetailPage id={Number(payrollDetail[1])} ledger="payroll" />;
    const mobilityDetail = path.match(/^\/mobility-fund\/(\d+)$/);
    if (mobilityDetail) return <PayrollDetailPage id={Number(mobilityDetail[1])} ledger="mobility" />;
    if (path === "/payroll") return <PayrollListPage ledger="payroll" />;
    if (path === "/mobility-fund") return <PayrollListPage ledger="mobility" />;
    return <DashboardPage />;
  })();

  const tint = routeTint(path);
  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={path}
          initial="initial"
          animate="enter"
          exit="exit"
          variants={PAGE_VARIANTS}
          transition={PAGE_TRANSITION}
          style={{ position: "relative", minHeight: "calc(100dvh - 14rem)" }}
        >
          {page}
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        {tint && (
          <motion.span
            key={`tint-${path}`}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
            className="pointer-events-none absolute inset-0 -z-0"
            style={{
              background: `radial-gradient(1200px 480px at 50% -8%, ${tint}, transparent 68%)`,
              mixBlendMode: "screen",
              borderRadius: "inherit",
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
